import { Stack } from "@mui/material";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthGuard, QueryCacheAuthBoundary } from "@bw/shared/components";
import { createQueryClient } from "../common/query/queryClient";
import { AppShell } from "./layouts/AppShell";
import { AuthLayout } from "./layouts/AuthLayout";
import { RequireProfile } from "./RequireProfile";
import { AuthLoadingFallback } from "./components/AuthLoadingFallback";
import { BusinessWorkspaceLink } from "./BusinessWorkspaceLink";
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from "../modules/auth";
import { GuardianLinkPage, OnboardingPage } from "../modules/onboarding";
import { KioskPage } from "../modules/kiosk";
import { HomePage } from "../modules/home";
import { PersonalCalendarPage, PlayerAgenda } from "../modules/agenda";
import { CommunityPlacePage, PlayPage } from "../modules/play";
import { CompeteLayout, MyCompetitionsPage } from "../modules/compete";
import { GamificationPage } from "../modules/gamification";
import { RewardsPage } from "../modules/rewards";
import {
  PerformancePage,
  MatchHistoryPage,
  MatchDetailPage,
  RankingsPage,
  MyStandingLabel,
  PlayerProfilePage,
} from "../modules/performance";
import {
  ProfileFriendActions,
  SocialPage,
  CommunityDetailPage,
  ChannelThreadPage,
  PostDetailPage,
  FriendsPage,
  BlockedUsersPage,
  InboxPage,
  InquiryInboxPage,
  InquiryThreadPage,
  ThreadPage,
} from "../modules/social";
import {
  MePage,
  MeAccountPage,
  MeInvitationsPage,
  MeSectionNavigation,
  ProfileEditPage,
} from "../modules/me";
import { NotificationsPage } from "../modules/notifications";
import { CalendarPage } from "../modules/calendar";
import {
  FacilityHomePage,
  BookingLayout,
  CourtSheetPage,
  ReviewPage,
  MyBookingsPage,
  BookingDetailPage,
  BookingInvitesInboxPage,
  PlayerReservationStateSync,
} from "../modules/booking";
import {
  CheckInCodePage,
  CheckInPage,
  DropInCheckInPage,
  DropInWaiverPage,
  DropInActiveSessionPage,
  QueueWaitingPage,
  CourtBoardPage,
  PartyInvitesInboxPage,
  PartyDetailPage,
} from "../modules/dropin";
import {
  FacilityProgramsPage as ClassesFacilityProgramsPage,
  DetailPage as ClassesDetailPage,
  EnrollPage as ClassesEnrollPage,
  MyClassesPage,
  LearningProgressPage,
} from "../modules/classes";
import {
  TransactionsPage,
  TransactionDetailPage,
  NotificationSettingsPage,
  DisplayPreferencesPage,
  PrivacyPage,
  PaymentMethodsPage,
  ModerationRecoveryPage,
  AccountRecoveryLayout,
} from "../modules/account";
import { ChildDetailPage, FamilyApprovalsPage } from "../modules/family";
import {
  CoachesListPage,
  CoachDetailPage,
  MyLessonsPage,
  CoachingRequestPage,
  CoachingExperiencePage,
  CoachingExperienceAccountLink,
  RateCoachPage,
} from "../modules/coaches";
import {
  TournamentsListPage,
  TournamentDetailPage,
  MyEntriesPage,
  TournamentCheckoutReturn,
  GuardianConsentPage,
  VenueDisplayExchangePage,
  VenueDisplayPage,
} from "../modules/tournaments";
import {
  MyLeaguesPage,
  LeagueStandingsPage,
  LeagueDirectoryPage,
  LeaguePublicDivisionPage,
} from "../modules/leagues";
import {
  FacilityMembershipsPage,
  PlanDetailPage,
  MyMembershipsPage,
  MembershipDetailPage,
} from "../modules/memberships";
import { SearchPage } from "../modules/search";
import { NotFoundPage } from "../modules/not-found";
import {
  MessagingProgramPage,
  PrivacyPolicyPage,
  SmsTermsPage,
  TermsOfServicePage,
} from "../modules/legal";

/**
 * Slice 1 route table.
 *
 * Three tiers:
 *   1. PUBLIC auth routes (/login, /register) under <AuthLayout> — OUTSIDE the
 *      AppShell so they never mount app chrome. An authed visitor is bounced
 *      away by useAuthRedirect inside the pages (no login loop).
 *   2. PUBLIC information/legal routes (/messaging-program, /terms,
 *      /sms-terms, /privacy) so carrier reviewers and auth copy never require
 *      a session.
 *   3. The GUARDED app tree wrapped in @bw/shared <AuthGuard/> (NO claim gate —
 *      any authenticated user is a valid player). Unauthed visitors are
 *      redirected to /login?returnUrl=<destination>.
 *
 * Slice 2 adds a profile-existence gate inside <AuthGuard>:
 *   - /onboarding is auth-gated but NOT behind <RequireProfile> (so an authed
 *     no-profile user can reach it without a redirect loop).
 *   - <RequireProfile> wraps the AppShell subtree: it reads getMyProfile and
 *     bounces a no-profile (404) user to /onboarding, otherwise mounts the app.
 *
 * The "*" catch-all stays a REAL NotFoundPage (honest 404) inside the guarded
 * shell — deliberately NOT a redirect to "/".
 */
// One client per app process (createQueryClient: staleTime 60s, retry 1). Owned
// HERE — not in app/providers — so <Router/> is a SELF-CONTAINED unit: it
// renders with only an AuthContext above it, which is exactly how the
// route-placement tests exercise the real route table (a QueryClientProvider
// wrapper would otherwise be a hidden test-only dependency). This mirrors the
// business app's router, which owns its single QueryClientProvider for the same
// reason. App composes <Router/> under ErrorBoundary + AuthProvider.
const queryClient = createQueryClient();

export function Router() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Wipes the React Query cache when the authed identity changes, so a same-
        tab sign-out → sign-in can never let the next user read the previous
        user's cached, user-scoped data (notifications/bookings/profile PII).
        Mounted INSIDE the client (it needs useQueryClient) and below the
        AuthProvider supplied by App (it needs useAuth). */}
      <QueryCacheAuthBoundary>
        <PlayerReservationStateSync />
        <BrowserRouter>
          <TournamentCheckoutReturn>
            <Routes>
              {/* Public auth pages — chrome-less, outside the guard. */}
              <Route element={<AuthLayout />}>
                <Route path="login" element={<LoginPage />} />
                <Route path="register" element={<RegisterPage />} />
                {/* The focused "request a reset link" screen (task 3 of the login
              rework) — reached via LoginPage's "Forgot password?" link. */}
                <Route
                  path="forgot-password"
                  element={<ForgotPasswordPage />}
                />
                {/* The password-reset email landing (?oobCode=...). Public by
              necessity: the user clicking the link is locked out. */}
                <Route path="reset-password" element={<ResetPasswordPage />} />
                {/* #1307: post-sign-up "verify your email" screen. Public (the new
              user has no session yet by design) with the Resend control. */}
                <Route path="verify-email" element={<VerifyEmailPage />} />
              </Route>

              {/* Public legal pages — Player-owned native-MUI presentation. */}
              <Route path="terms" element={<TermsOfServicePage />} />
              <Route
                path="messaging-program"
                element={<MessagingProgramPage />}
              />
              <Route path="sms-terms" element={<SmsTermsPage />} />
              <Route path="privacy" element={<PrivacyPolicyPage />} />

              {/* PUBLIC guardian-consent confirmation (#1684 / #1692) — chrome-less and
            OUTSIDE the AuthGuard: a held minor's parent/guardian arrives from the
            emailed signed link (#token=) and is NOT necessarily a logged-in BW
            user. The page reads the fragment token once, scrubs the URL, confirms consent, and surfaces the
            off-session-charge decline (402 PAYMENT_NEEDS_ATTENTION) / invalid-link
            states itself — no login wall, no app shell. */}
              <Route
                path="guardian-consent"
                element={<GuardianConsentPage />}
              />

              {/* A tournament-scoped display uses a BFF-held capability session,
            never a player account. Both routes stay chrome-less and outside
            AuthGuard so a projector can recover after a reconnect without a
            player login or application navigation. */}
              <Route
                path="tournament-display"
                element={<VenueDisplayExchangePage />}
              />
              <Route
                path="tournament-display/:tournamentId"
                element={<VenueDisplayPage />}
              />
              {/* The entrance scan kiosk (#2691) mirrors the venue display: a
            revocable BFF-held device session, chrome-less and outside
            AuthGuard, so a tablet re-pairs on 401 instead of a player login. */}
              <Route path="kiosk" element={<KioskPage />} />

              {/* PUBLIC-BROWSE home (`/` index) — OUTSIDE the AuthGuard so an anonymous
            visitor lands on the dashboard inside the app shell (not a login wall).
            The AppBar carries a Sign-in affordance and the home's token-scoped
            cards are auth-gated (no `/me/*` 401s). It reuses the SAME AppShell as
            the guarded tree — the shell is a pure layout, so mounting it here vs.
            under the guard is identical chrome. Personal nav targets (Play / Me /
            Compete / Social) still route INTO the guarded tree below, which
            bounces an anonymous click to /login. Home and tournament discovery
            deliberately remain outside RequireProfile as bounded read-only
            destinations for profile-incomplete or restricted accounts. */}
              <Route element={<AppShell />}>
                <Route index element={<HomePage agenda={<PlayerAgenda variant="home" />} />} />
                <Route path="play" element={<PlayPage />} />
                <Route path="place/:placeId" element={<CommunityPlacePage />} />
                <Route path="search" element={<SearchPage />} />
                <Route
                  path="facility/:facilityId/coaches"
                  element={<CoachesListPage />}
                />
                <Route
                  path="coaches/:coachProfileId"
                  element={<CoachDetailPage />}
                />
                <Route
                  path="facility/:facilityId"
                  element={<FacilityHomePage />}
                />
                <Route
                  path="facility/:facilityId/memberships"
                  element={<FacilityMembershipsPage />}
                />
                <Route
                  path="classes/:facilityId"
                  element={<ClassesFacilityProgramsPage />}
                />
                <Route
                  path="classes/:facilityId/:programId"
                  element={<ClassesDetailPage />}
                />
                {/* Tournament discovery and public detail are readable without a
                session. Registration actions handle anonymous users explicitly
                and send them through login with a return URL. */}
                <Route element={<CompeteLayout />}>
                  <Route
                    path="compete/tournaments"
                    element={<TournamentsListPage />}
                  />
                  <Route
                    path="compete/leagues"
                    element={<LeagueDirectoryPage />}
                  />
                  <Route
                    path="compete/leagues/divisions/:divisionId"
                    element={<LeaguePublicDivisionPage />}
                  />
                  <Route
                    path="tournaments/:tournamentId"
                    element={<TournamentDetailPage />}
                  />
                </Route>
              </Route>

              {/* Guarded app tree — AuthGuard with no claim gate. */}
              <Route
                element={
                  <AuthGuard loadingFallback={<AuthLoadingFallback />} />
                }
              >
                {/* Onboarding: auth-gated, but NOT behind RequireProfile, so an authed
              user with no profile reaches it (no redirect loop). */}
                <Route path="onboarding" element={<OnboardingPage />} />

                {/* Safety recovery is authenticated but deliberately outside the
              profile and interaction shell gates. A restricted player must be
              able to inspect and appeal the token-scoped active ban; an active
              player sees the same pages inside the app shell. */}
                <Route element={<AccountRecoveryLayout appShell={<AppShell />} />}>
                  <Route
                    path="me/moderation"
                    element={<ModerationRecoveryPage />}
                  />
                  <Route path="me/privacy" element={<PrivacyPage />} />
                </Route>

                {/* Profile-existence gate wrapping the app shell + routes. */}
                <Route element={<RequireProfile />}>
                  <Route element={<AppShell />}>
                    {/* Booking journey — reached THROUGH Play (no new top-level nav).
                  Both routes inherit the AuthGuard + RequireProfile gate. */}
                    {/* The facility HOMEPAGE — the hub discovery now lands on (the
                  cards + map markers route here, not straight into the grid). It
                  carries the "Book a court" CTA into book/:facilityId below.
                  Sibling of book/:facilityId; same AuthGuard → RequireProfile →
                  AppShell gate. */}
                    {/* Membership journey — reached THROUGH the facility homepage's
                  "Memberships" chip (no new top-level nav). The per-facility plan
                  catalog, then a plan's detail + inline-Stripe purchase. The
                  STATIC `memberships` segment sits under the same
                  `facility/:facilityId` prefix as the homepage above; React
                  Router matches the deeper path here. Both inherit the
                  AuthGuard → RequireProfile → AppShell gate. */}
                    <Route
                      path="facility/:facilityId/memberships/:planId"
                      element={<PlanDetailPage />}
                    />
                    {/* Booking flow — the CartProvider is LIFTED to BookingLayout so a
                  single cart is shared across the sheet, the review/checkout
                  screen, and my-bookings (3b/3c). A sheet-scoped provider would
                  leave review with an empty cart. */}
                    <Route path="book/:facilityId" element={<BookingLayout />}>
                      <Route index element={<CourtSheetPage />} />
                      <Route path="review" element={<ReviewPage />} />
                      <Route path="bookings" element={<MyBookingsPage />} />
                      {/* The per-booking detail HUB (slice δ) — the roster + host-only
                    add/remove + a check-in QR for one court booking. A child of
                    the bookings LIST so a row links into it (no new top-level
                    nav); inherits the AuthGuard → RequireProfile → AppShell gate.
                    The static `bookings` segment above never collides with this
                    `:bookingId`. */}
                      <Route
                        path="bookings/:bookingId"
                        element={<BookingDetailPage />}
                      />
                    </Route>
                    {/* Drop-in journey — also reached THROUGH Play (no new
                  top-level nav). Discovery list + facility-confirmed check-in.
                  Both inherit the AuthGuard + RequireProfile gate. */}
                    <Route
                      path="dropin/:facilityId"
                      element={<DropInCheckInPage />}
                    />
                    <Route
                      path="dropin/:facilityId/waiver"
                      element={<DropInWaiverPage />}
                    />
                    {/* Check in at the entrance kiosk (#2691): what the player can
                  show (paid bookings, passes to "Redeem one", unpaid drop-in),
                  then the refreshing single-use code for one redeemed visit. */}
                    <Route
                      path="dropin/:facilityId/check-in"
                      element={<CheckInPage />}
                    />
                    <Route
                      path="dropin/:facilityId/check-in/:subscriptionId"
                      element={<CheckInCodePage />}
                    />
                    {/* Post-check-in queue flow — reached THROUGH check-in (no new
                  top-level nav). The active-session hub, then the court-queue
                  waiting view (which yields to the "you're up" assigned surface
                  in place when the entry flips to assigned). All inherit the
                  AuthGuard + RequireProfile gate. */}
                    <Route
                      path="dropin/:facilityId/session"
                      element={<DropInActiveSessionPage />}
                    />
                    <Route
                      path="dropin/:facilityId/queue/:entryId"
                      element={<QueueWaitingPage />}
                    />
                    {/* Self-serve COURT BOARD (Phase 2 P2.S2) — reached THROUGH the
                  active-session hub (no new top-level nav). Lists each court's
                  live state and lets a player start / join / leave a game. A
                  sibling of dropin/:facilityId/session; the static last segment
                  (`court-board`) never collides with `/session` or `/queue/:id`.
                  Inherits the AuthGuard → RequireProfile → AppShell gate. */}
                    <Route
                      path="dropin/:facilityId/court-board"
                      element={<CourtBoardPage />}
                    />
                    {/* Me owns the invitation destination; its URL-backed selector
                  composes the existing court and drop-in inboxes. Domain pages
                  retain their response flows under AuthGuard/RequireProfile. */}
                    <Route
                      path="me/invitations"
                      element={
                        <MeInvitationsPage
                          courtInvitations={<BookingInvitesInboxPage />}
                          openPlayInvitations={<PartyInvitesInboxPage />}
                        />
                      }
                    />
                    <Route
                      path="dropin/party/:partyId"
                      element={<PartyDetailPage />}
                    />
                    {/* Classes & clinics journey — reached THROUGH Play (no new
                  top-level nav), mirroring booking + drop-in. Facility-first
                  discovery, then a facility's published programs, a program's
                  detail + tracks, and a per-track enroll surface. All inherit
                  the AuthGuard + RequireProfile gate. */}
                    <Route
                      path="classes/:facilityId/:programId/enroll/:trackId"
                      element={<ClassesEnrollPage />}
                    />
                    <Route
                      path="classes/:facilityId/:programId/learning/:enrollmentId"
                      element={<LearningProgressPage />}
                    />
                    {/* Coaches discovery journey — reached THROUGH Play (no new
                  top-level nav), mirroring booking + drop-in + classes. The
                  coach list, then a coach's detail (profile / stats / services /
                  affiliations) with a request-a-lesson action. Both inherit the
                  AuthGuard → RequireProfile → AppShell gate. */}
                    <Route
                      path="me/coaching-experience"
                      element={<CoachingExperiencePage />}
                    />
                    <Route
                      path="classes/:programId/rate-coach"
                      element={<RateCoachPage />}
                    />
                    {/* The player's CROSS-TOURNAMENT entry history — reached THROUGH
                  the tournaments list (no new top-level nav). The STATIC route
                  remains private even though tournament discovery and detail are
                  public above. */}
                    <Route element={<CompeteLayout />}>
                      <Route
                        path="compete/mine"
                        element={
                          <MyCompetitionsPage
                            tournamentEntries={<MyEntriesPage />}
                            leagueEntries={<MyLeaguesPage />}
                          />
                        }
                      />
                      <Route
                        path="compete/leagues/divisions/:divisionId/standings"
                        element={<LeagueStandingsPage />}
                      />
                    </Route>
                    <Route
                      path="me/performance"
                      element={
                        <Stack spacing={2.5} sx={{ maxWidth: 720 }}>
                          <MeSectionNavigation active="performance" />
                          <PerformancePage />
                        </Stack>
                      }
                    />
                    <Route
                      path="me/achievements"
                      element={
                        <Stack spacing={2.5} sx={{ maxWidth: 720 }}>
                          <MeSectionNavigation active="performance" />
                          <GamificationPage />
                        </Stack>
                      }
                    />
                    <Route
                      path="me/rewards"
                      element={
                        <Stack spacing={2.5} sx={{ maxWidth: 720 }}>
                          <MeSectionNavigation active="activity" />
                          <RewardsPage />
                        </Stack>
                      }
                    />
                    <Route
                      path="me/performance/matches"
                      element={<MatchHistoryPage />}
                    />
                    <Route
                      path="me/performance/matches/:matchId"
                      element={<MatchDetailPage />}
                    />
                    <Route element={<CompeteLayout />}>
                      <Route path="rankings" element={<RankingsPage />} />
                    </Route>
                    <Route
                      path="players/:userId"
                      element={
                        <PlayerProfilePage
                          renderFriendActions={(playerId, playerName) => (
                            <ProfileFriendActions playerId={playerId} playerName={playerName} />
                          )}
                        />
                      }
                    />
                    {/* Social journey — the Communities hub (Discover / My Communities
                  tabs over one communityApi.list() fetch). A community's detail +
                  join/leave surface is reached THROUGH a hub card (no new
                  top-level nav). Both inherit the AuthGuard → RequireProfile →
                  AppShell gate. */}
                    <Route path="community" element={<SocialPage />} />
                    {/* Home is the one canonical network feed. Retain the old
                  route only as a replace-navigation for saved links. */}
                    <Route
                      path="community/feed"
                      element={<Navigate to="/" replace />}
                    />
                    <Route path="me/friends" element={<FriendsPage />} />
                    {/* Retained deep-link alias; Friends is discovered from Me. */}
                    <Route path="community/friends" element={<FriendsPage />} />
                    <Route
                      path="me/safety/blocked"
                      element={<BlockedUsersPage />}
                    />
                    {/* Messages is globally available from the app bar. */}
                    <Route path="community/messages" element={<InboxPage />} />
                    <Route
                      path="community/inquiries"
                      element={<InquiryInboxPage />}
                    />
                    <Route
                      path="community/inquiries/:conversationId"
                      element={<InquiryThreadPage />}
                    />
                    {/* A single conversation's thread / chat view (Slice F2) — reached
                  THROUGH an inbox row (no new top-level nav), sibling of the
                  inbox. Inherits the AuthGuard → RequireProfile → AppShell gate. */}
                    <Route
                      path="community/messages/:conversationId"
                      element={<ThreadPage />}
                    />
                    <Route
                      path="community/communities/:communityId"
                      element={<CommunityDetailPage />}
                    />
                    <Route
                      path="community/communities/:communityId/channels/:conversationId"
                      element={<ChannelThreadPage />}
                    />
                    {/* A post's detail + comment thread (Slice 2, READ-only) — reached
                  THROUGH a community feed PostCard (no new top-level nav). Inherits
                  the AuthGuard → RequireProfile → AppShell gate. */}
                    <Route
                      path="community/posts/:postId"
                      element={<PostDetailPage />}
                    />
                    {/* Me journey — the personal upcoming calendar, edit page, and
                  cross-facility court-booking record are reached THROUGH /me,
                  mirroring booking's book/:facilityId nesting. All inherit the
                  AuthGuard + RequireProfile gate. /me/bookings is the player's
                  bookings across ALL facilities (the per-facility list lives at
                  /book/:facilityId/bookings). */}
                    <Route
                      path="me"
                      element={
                        <MePage
                          agenda={<PlayerAgenda variant="activity" />}
                          standing={<MyStandingLabel />}
                        />
                      }
                    />
                    <Route
                      path="me/account"
                      element={
                        <MeAccountPage
                          businessWorkspaceLink={<BusinessWorkspaceLink />}
                          coachingExperienceLink={
                            <CoachingExperienceAccountLink />
                          }
                        />
                      }
                    />
                    {/* Notifications inbox — a top-level guarded surface (sibling of
                  /me) inside AuthGuard → RequireProfile → AppShell. The bell in
                  the AppBar links here. */}
                    <Route
                      path="notifications"
                      element={<NotificationsPage />}
                    />
                    <Route path="me/edit" element={<ProfileEditPage />} />
                    <Route
                      path="me/calendar"
                      element={<PersonalCalendarPage />}
                    />
                    <Route path="me/bookings" element={<CalendarPage />} />
                    {/* The player's own enrollments — reached THROUGH /me, mirroring
                  /me/bookings. Cancel + waitlist-promotion claim/decline. */}
                    <Route path="me/classes" element={<MyClassesPage />} />
                    {/* The player's lesson requests — reached THROUGH /me, mirroring
                  /me/classes. An ACCEPTED + unpaid lesson exposes the inline
                  facility-scoped Stripe pay panel (the same spine class enroll
                  uses); paying re-lists the truth → the row flips to Paid. */}
                    <Route path="me/lessons" element={<MyLessonsPage />} />
                    <Route
                      path="me/lessons/:requestId"
                      element={<CoachingRequestPage />}
                    />
                    {/* The player's CROSS-FACILITY league history (read-only) — reached
                  THROUGH /me, mirroring /me/classes + /me/bookings. The league
                  analogue of /tournaments/my-entries. Inherits the
                  AuthGuard → RequireProfile → AppShell gate. */}
                    {/* A division's STANDINGS detail (S2, read-only) — the drill-down
                  reached THROUGH a My Leagues card (no new top-level nav). The
                  card forwards the league/season/division/facility NAMES via
                  React Router <Link state={...}> so the page needs no
                  GET /leagues/divisions/{id} resolver; it falls back to a generic
                  "Division standings" title on a direct-URL hit. Inherits the
                  AuthGuard → RequireProfile → AppShell gate. */}
                    {/* League DISCOVERY browse (S3b) — the facility-scoped browse +
                  self-register, reached THROUGH the booking facility-select page's
                  per-facility "Leagues" link (by path, no cross-module import),
                  mirroring book/:facilityId + dropin/:facilityId + classes/:facilityId.
                  The LITERAL `divisions` first segment of the standings route above
                  RANKS ABOVE this `:facilityId` param (React Router scores static
                  segments over dynamic), so /leagues/divisions/x still resolves to
                  LeagueStandingsPage — this route only matches a real facility id.
                  Inherits the AuthGuard → RequireProfile → AppShell gate. */}
                    {/* Account → payment history + saved cards + Privacy & data (CCPA
                  self-serve) — reached THROUGH /me (no new top-level nav),
                  mirroring /me/bookings + /me/classes. All inherit the
                  AuthGuard → RequireProfile → AppShell gate. /me/payments is the
                  player's CROSS-ORG saved cards (#1392). */}
                    <Route
                      path="me/transactions"
                      element={<TransactionsPage />}
                    />
                    <Route
                      path="me/transactions/:transactionId"
                      element={<TransactionDetailPage />}
                    />
                    <Route
                      path="me/notifications"
                      element={<NotificationSettingsPage />}
                    />
                    <Route
                      path="me/preferences"
                      element={<DisplayPreferencesPage />}
                    />
                    <Route
                      path="me/payments"
                      element={<PaymentMethodsPage />}
                    />
                    {/* Membership management home — reached THROUGH /me (a tile on the
                  MePage grid), mirroring /me/bookings + /me/transactions. The
                  list, then a single membership's detail + inline-confirm cancel /
                  reactivate. The STATIC `memberships` segment never collides with
                  the `:subscriptionId` detail below. Both inherit the
                  AuthGuard → RequireProfile → AppShell gate. */}
                    <Route
                      path="me/memberships"
                      element={<MyMembershipsPage />}
                    />
                    <Route
                      path="me/memberships/:subscriptionId"
                      element={<MembershipDetailPage />}
                    />
                    {/* Family approvals (COPPA parent-managed consent, #1309 Part A) —
                  a guarded top-level surface reached by an authenticated PARENT
                  from the request email ("<child> wants you to approve their
                  account"). Lists the parent's pending child-link requests and
                  lets them approve (records the guardian waiver server-side) /
                  revoke. Inherits AuthGuard → RequireProfile → AppShell. */}
                    <Route
                      path="me/family/approvals"
                      element={<FamilyApprovalsPage />}
                    />
                    {/* One child a guardian manages (multi-guardian, #2691):
                  co-guardians, invites, account access, leave. Keyed by the
                  viewer's own link id, never a child id. */}
                    <Route
                      path="me/family/children/:linkId"
                      element={<ChildDetailPage />}
                    />
                    {/* A look-only minor (#2691) asks a parent or guardian to
                  approve the account from inside the app. */}
                    <Route path="me/guardian" element={<GuardianLinkPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Route>
              </Route>
            </Routes>
          </TournamentCheckoutReturn>
        </BrowserRouter>
      </QueryCacheAuthBoundary>
    </QueryClientProvider>
  );
}
